import { useEffect, useMemo, useState, useCallback } from 'react'
import ScheduleEndpoints from '../../../endpoints/ScheduleEndpoints'
import { Event as EventModel } from '../../../model/Event'
import {
  computeEventSetupStatus,
  EventSetupStatus,
} from './eventSetupStatus'

interface UseEventSetupStatusOptions {
  /**
   * Fetch GET /event/{id}/schedulable so the Staff step can resolve. Leave off
   * on pages that don't need it — the step then reports `unknown` rather than
   * a false "incomplete".
   */
  includeStaff?: boolean
  /**
   * Supply the count directly when the caller already has the schedulable list
   * in hand (EventScheduleModern does) so we don't fetch it twice.
   */
  schedulableCount?: number
  shiftCount?: number
}

interface UseEventSetupStatusResult {
  status: EventSetupStatus
  /** True while the schedulable list is in flight. */
  loadingStaff: boolean
  /** Re-fetch the schedulable count, e.g. after returning from provisioning. */
  refreshStaff: () => Promise<void>
}

export function useEventSetupStatus(
  event: EventModel | null | undefined,
  options: UseEventSetupStatusOptions = {}
): UseEventSetupStatusResult {
  const { includeStaff = false, schedulableCount, shiftCount } = options
  const eventId = event?.id
  const shouldFetch = includeStaff && schedulableCount === undefined && !!eventId

  const [fetchedCount, setFetchedCount] = useState<number | undefined>(undefined)
  const [loadingStaff, setLoadingStaff] = useState(false)

  const load = useCallback(
    async (signal?: AbortSignal) => {
      if (!eventId) return
      setLoadingStaff(true)
      try {
        const schedulable = await ScheduleEndpoints.getSchedulableForEvent(eventId)
        if (signal?.aborted) return
        setFetchedCount(schedulable?.length ?? 0)
      } catch (error) {
        // Non-critical: the Staff step just stays "unknown" rather than lying
        // about being incomplete.
        console.error('Failed to load schedulable staff for setup status:', error)
      } finally {
        if (!signal?.aborted) setLoadingStaff(false)
      }
    },
    [eventId]
  )

  useEffect(() => {
    if (!shouldFetch) return
    const controller = new AbortController()
    setFetchedCount(undefined)
    load(controller.signal)
    return () => controller.abort()
  }, [shouldFetch, load])

  const refreshStaff = useCallback(() => load(), [load])

  const effectiveCount = schedulableCount !== undefined ? schedulableCount : fetchedCount

  const status = useMemo(
    () =>
      computeEventSetupStatus(event, {
        schedulableCount: effectiveCount,
        shiftCount,
      }),
    [event, effectiveCount, shiftCount]
  )

  return { status, loadingStaff, refreshStaff }
}
